/**
 * Structural + semantic analysis of a rule document (docs/RULE_ENGINE.md §2–§5): produces the
 * validation errors and, for every leaf, a compiled form (operator family + parsed operands)
 * used by the evaluator. Never executes anything from the document.
 */
import { checkRef, ENGINE_REFS, isEngineRef, lookupRef } from './refs.js';
import { parseDate, parseTime, MS_PER_DAY } from './time.js';
export const RULE_SCHEMA = 'signetix.rule';
export const SUPPORTED_RULE_VERSIONS = [1];
export const MAX_DEPTH = 4;
export const MAX_LEAVES = 50;
export const MAX_TEXT_VALUE_LENGTH = 1024;
export const OPERATORS = [
    'eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'between', 'contains', 'not_contains', 'starts_with', 'ends_with',
    'is_true', 'is_false', 'empty', 'not_empty', 'before', 'after', 'today', 'freshness_is',
];
export const FRESHNESS_VALUES = ['fresh', 'stale', 'expired', 'unavailable'];
export const FRESHNESS_POLICIES = ['require_fresh', 'allow_stale'];
export const VALUE_TYPES = ['text', 'number', 'boolean', 'date', 'datetime', 'time', 'list', 'url', 'color', 'media', 'json', 'any'];
/** Declared types an operator family accepts (dynamic `json`/`any`/unrecognised types accept all). */
const FAMILY_TYPES = {
    number: ['number'],
    text: ['text', 'url', 'color', 'media', 'date'],
    contains: ['text', 'url', 'color', 'media', 'date', 'list'],
    contains_scalar: ['list'],
    boolean: ['boolean'],
    list: ['list'],
    date: ['date', 'datetime'],
    time: ['time', 'datetime'],
};
/** True when a declared type is compatible with the leaf's family. */
export function typeCompatible(leaf, type) {
    if (type === undefined || type === 'json' || type === 'any' || VALUE_TYPES.indexOf(type) < 0)
        return true;
    if (leaf.family === 'freshness')
        return true;
    const key = leaf.family === 'contains' && typeof leaf.scalar !== 'string' ? 'contains_scalar' : leaf.family;
    return FAMILY_TYPES[key].indexOf(type) >= 0;
}
const LEAF_FIELDS = ['ref', 'op', 'value', 'unit', 'case_sensitive', 'freshness'];
const UNIT_RE = /^[a-z][a-z0-9_]{0,31}$/;
export function isObject(x) {
    return typeof x === 'object' && x !== null && !Array.isArray(x);
}
function has(o, k) {
    return Object.prototype.hasOwnProperty.call(o, k);
}
export function pointerToken(k) {
    return k.replace(/~/g, '~0').replace(/\//g, '~1');
}
/** Length in Unicode code points (surrogate pairs count once), as in PHP `mb_strlen`. */
export function codePointLength(s) {
    let n = 0;
    for (let i = 0; i < s.length; i++) {
        const c = s.charCodeAt(i);
        if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
            const d = s.charCodeAt(i + 1);
            if (d >= 0xdc00 && d <= 0xdfff)
                i++;
        }
        n++;
    }
    return n;
}
function isFiniteNumber(x) {
    return typeof x === 'number' && isFinite(x);
}
class Analyser {
    constructor(opts) {
        this.opts = opts;
        this.errors = [];
        this.leaves = [];
        this.leafCount = 0;
    }
    err(path, code, message) {
        this.errors.push({ path, code, message });
    }
    root(rule) {
        if (!isObject(rule)) {
            this.err('', 'not_object', 'A rule must be a JSON object.');
            return false;
        }
        let stop = false;
        if (rule['schema'] !== RULE_SCHEMA) {
            this.err('/schema', 'invalid_schema', `"schema" must be "${RULE_SCHEMA}".`);
            stop = true;
        }
        const v = rule['version'];
        if (typeof v !== 'number' || !isFinite(v) || Math.floor(v) !== v) {
            this.err('/version', 'invalid_version', '"version" must be an integer.');
            stop = true;
        }
        else if (SUPPORTED_RULE_VERSIONS.indexOf(v) < 0) {
            this.err('/version', 'unsupported_version', `Rule version ${v} is not supported (supported: ${SUPPORTED_RULE_VERSIONS.join(', ')}).`);
            stop = true;
        }
        if (stop)
            return false;
        const body = {};
        for (const k of Object.keys(rule))
            if (k !== 'schema' && k !== 'version')
                body[k] = rule[k];
        if (!has(body, 'all') && !has(body, 'any') && !has(body, 'not')) {
            this.err('', 'invalid_group', 'The rule root must be a group: exactly one of "all", "any" or "not".');
            return true;
        }
        this.group(body, '', 1);
        if (this.leafCount > MAX_LEAVES)
            this.err('', 'max_leaves', `A rule may contain at most ${MAX_LEAVES} conditions (found ${this.leafCount}).`);
        return true;
    }
    node(n, path, depth) {
        if (!isObject(n)) {
            this.err(path, 'not_object', 'A condition or group must be a JSON object.');
            return;
        }
        if (has(n, 'all') || has(n, 'any') || has(n, 'not'))
            this.group(n, path, depth);
        else
            this.leaf(n, path);
    }
    group(g, path, depth) {
        const keys = ['all', 'any', 'not'].filter((k) => has(g, k));
        if (keys.length !== 1) {
            this.err(path, 'invalid_group', 'A group has exactly one of "all", "any" or "not".');
            return;
        }
        if (depth > MAX_DEPTH) {
            this.err(path, 'max_depth', `Groups may be nested at most ${MAX_DEPTH} levels deep.`);
            return;
        }
        for (const k of Object.keys(g)) {
            if (k !== keys[0])
                this.err(`${path}/${pointerToken(k)}`, 'unknown_field', `Unknown field "${k}" on a group.`);
        }
        const key = keys[0];
        const body = g[key];
        const p = `${path}/${key}`;
        if (key === 'not') {
            if (!isObject(body)) {
                this.err(p, 'invalid_group', '"not" takes a single condition or group (an object).');
                return;
            }
            this.node(body, p, depth + 1);
            return;
        }
        if (!Array.isArray(body)) {
            this.err(p, 'invalid_group', `"${key}" takes a list of conditions or groups.`);
            return;
        }
        if (body.length === 0) {
            this.err(p, 'empty_group', `"${key}" must contain at least one condition.`);
            return;
        }
        for (let i = 0; i < body.length; i++)
            this.node(body[i], `${p}/${i}`, depth + 1);
    }
    leaf(l, path) {
        this.leafCount++;
        const before = this.errors.length;
        for (const k of Object.keys(l)) {
            if (LEAF_FIELDS.indexOf(k) < 0)
                this.err(`${path}/${pointerToken(k)}`, 'unknown_field', `Unknown field "${k}" on a condition.`);
        }
        // ref
        let refOk = false;
        const ref = l['ref'];
        if (!has(l, 'ref'))
            this.err(`${path}/ref`, 'missing_field', 'A condition needs a "ref".');
        else if (typeof ref !== 'string')
            this.err(`${path}/ref`, 'invalid_ref', '"ref" must be a string.');
        else {
            const c = checkRef(ref);
            if (c.ok)
                refOk = true;
            else
                this.err(`${path}/ref`, c.code, c.message);
        }
        // op
        const op = l['op'];
        if (!has(l, 'op')) {
            this.err(`${path}/op`, 'missing_field', 'A condition needs an "op".');
            return;
        }
        if (typeof op !== 'string' || OPERATORS.indexOf(op) < 0) {
            this.err(`${path}/op`, 'unknown_operator', `Unknown operator "${String(op)}".`);
            return;
        }
        const leaf = {
            path, ref: typeof ref === 'string' ? ref : '', op: op, family: 'number',
            n1: 0, n2: 0, scalar: null, d1: 0, d2: 0, t1: 0, t2: 0, caseSensitive: false, unit: null, policy: 'require_fresh',
        };
        const familyOk = this.value(l, leaf);
        this.options(l, leaf, familyOk);
        if (familyOk && refOk)
            this.types(leaf);
        if (this.errors.length === before)
            this.leaves.push(leaf);
    }
    /** Parses `value` for the operator; sets the family. False when the value is unusable. */
    value(l, leaf) {
        const p = `${leaf.path}/value`;
        const present = has(l, 'value');
        const v = l['value'];
        const op = leaf.op;
        const none = (family) => {
            leaf.family = family;
            if (present) {
                this.err(p, 'invalid_value', `"${op}" takes no value.`);
                return false;
            }
            return true;
        };
        const textOk = (s) => {
            if (typeof s !== 'string')
                return false;
            if (s.length > MAX_TEXT_VALUE_LENGTH && codePointLength(s) > MAX_TEXT_VALUE_LENGTH) {
                this.err(p, 'invalid_value', `Text values are limited to ${MAX_TEXT_VALUE_LENGTH} characters.`);
                return false;
            }
            return true;
        };
        switch (op) {
            case 'is_true':
            case 'is_false':
                return none('boolean');
            case 'empty':
            case 'not_empty':
                return none('list');
            case 'today':
                return none('date');
            default:
                break;
        }
        if (!present) {
            this.err(p, 'missing_value', `"${op}" needs a value.`);
            return false;
        }
        const bad = (msg) => {
            this.err(p, 'invalid_value', msg);
            return false;
        };
        switch (op) {
            case 'eq':
            case 'ne':
                if (isFiniteNumber(v)) {
                    leaf.family = 'number';
                    leaf.n1 = v;
                    return true;
                }
                if (typeof v === 'string') {
                    if (!textOk(v))
                        return false;
                    leaf.family = 'text';
                    leaf.scalar = v;
                    return true;
                }
                return bad(`"${op}" compares with a number or a text value.`);
            case 'gt':
            case 'lt':
            case 'gte':
            case 'lte':
                if (!isFiniteNumber(v))
                    return bad(`"${op}" compares with a number.`);
                leaf.family = 'number';
                leaf.n1 = v;
                return true;
            case 'starts_with':
            case 'ends_with':
                if (typeof v !== 'string')
                    return bad(`"${op}" takes a text value.`);
                if (!textOk(v))
                    return false;
                leaf.family = 'text';
                leaf.scalar = v;
                return true;
            case 'contains':
            case 'not_contains':
                if (typeof v === 'string') {
                    if (!textOk(v))
                        return false;
                }
                else if (!isFiniteNumber(v) && typeof v !== 'boolean') {
                    return bad(`"${op}" takes a text, number or boolean value.`);
                }
                leaf.family = 'contains';
                leaf.scalar = v;
                return true;
            case 'before':
            case 'after': {
                if (typeof v === 'string') {
                    const d = parseDate(v);
                    if (d !== null) {
                        leaf.family = 'date';
                        leaf.d1 = d;
                        return true;
                    }
                    const t = parseTime(v);
                    if (t !== null) {
                        leaf.family = 'time';
                        leaf.t1 = t;
                        return true;
                    }
                }
                return bad(`"${op}" takes a date (YYYY-MM-DD) or a time (HH:mm[:ss]).`);
            }
            case 'between': {
                if (!Array.isArray(v) || v.length !== 2)
                    return bad('"between" takes a list of exactly two bounds.');
                const a = v[0];
                const b = v[1];
                if (isFiniteNumber(a) && isFiniteNumber(b)) {
                    leaf.family = 'number';
                    leaf.n1 = a;
                    leaf.n2 = b;
                    if (a > b) {
                        this.err(p, 'invalid_range', '"between" needs lower ≤ upper.');
                        return false;
                    }
                    return true;
                }
                if (typeof a === 'string' && typeof b === 'string') {
                    const da = parseDate(a);
                    const db = parseDate(b);
                    if (da !== null && db !== null) {
                        leaf.family = 'date';
                        leaf.d1 = da;
                        leaf.d2 = db;
                        if (da > db) {
                            this.err(p, 'invalid_range', '"between" needs the earlier date first.');
                            return false;
                        }
                        return true;
                    }
                    const ta = parseTime(a);
                    const tb = parseTime(b, true);
                    if (ta !== null && tb !== null) {
                        leaf.family = 'time';
                        leaf.t1 = ta;
                        leaf.t2 = tb === 0 ? MS_PER_DAY : tb;
                        return true;
                    }
                }
                return bad('"between" takes two numbers, two dates (YYYY-MM-DD) or two times (HH:mm[:ss]; end may be 24:00).');
            }
            case 'freshness_is':
                if (typeof v !== 'string' || FRESHNESS_VALUES.indexOf(v) < 0)
                    return bad('"freshness_is" takes fresh, stale, expired or unavailable.');
                leaf.family = 'freshness';
                leaf.scalar = v;
                return true;
            default:
                return bad('Unsupported operator.');
        }
    }
    options(l, leaf, familyOk) {
        const p = leaf.path;
        if (has(l, 'case_sensitive')) {
            const cs = l['case_sensitive'];
            if (typeof cs !== 'boolean')
                this.err(`${p}/case_sensitive`, 'invalid_option', '"case_sensitive" must be true or false.');
            else if (familyOk && leaf.family !== 'text' && !(leaf.family === 'contains' && typeof leaf.scalar === 'string')) {
                this.err(`${p}/case_sensitive`, 'invalid_option', '"case_sensitive" applies to text comparisons only.');
            }
            else
                leaf.caseSensitive = cs;
        }
        if (has(l, 'unit')) {
            const u = l['unit'];
            if (typeof u !== 'string' || !UNIT_RE.test(u))
                this.err(`${p}/unit`, 'invalid_option', '"unit" must be a unit identifier like "celsius".');
            else if (familyOk && leaf.family !== 'number')
                this.err(`${p}/unit`, 'invalid_option', '"unit" applies to number comparisons only.');
            else
                leaf.unit = u;
        }
        if (has(l, 'freshness')) {
            const f = l['freshness'];
            if (typeof f !== 'string' || FRESHNESS_POLICIES.indexOf(f) < 0)
                this.err(`${p}/freshness`, 'invalid_option', '"freshness" must be "require_fresh" or "allow_stale".');
            else if (leaf.op === 'freshness_is')
                this.err(`${p}/freshness`, 'invalid_option', '"freshness" cannot be combined with "freshness_is".');
            else
                leaf.policy = f;
        }
    }
    types(leaf) {
        let type;
        if (isEngineRef(leaf.ref))
            type = ENGINE_REFS[leaf.ref];
        else if (this.opts.refTypes !== undefined || this.opts.knownRefs !== undefined) {
            const t = this.opts.refTypes ? lookupRef(this.opts.refTypes, leaf.ref) : { found: false, value: undefined };
            let known = t.found;
            if (!known && this.opts.knownRefs) {
                const m = {};
                for (const k of this.opts.knownRefs)
                    m[k] = true;
                known = lookupRef(m, leaf.ref).found;
            }
            if (!known) {
                this.err(`${leaf.path}/ref`, 'unknown_ref', `Unknown reference "${leaf.ref}".`);
                return;
            }
            type = t.found ? t.value : undefined;
        }
        if (!typeCompatible(leaf, type)) {
            this.err(`${leaf.path}/op`, 'operator_type_mismatch', `"${leaf.op}" cannot be used with a ${String(type)} value.`);
        }
    }
}
export function analyse(rule, opts = {}) {
    const a = new Analyser(opts);
    const structural = a.root(rule);
    return { errors: a.errors, leaves: a.leaves, versionError: !structural && a.errors.some((e) => e.code !== 'not_object') };
}
//# sourceMappingURL=compile.js.map